import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
    setSelectedPortfolioRequest, addPortfolioSuccess, deletePortfolioSuccess, getPortfolioWithVesselsSuccess, getPortfoliosSuccess, setPortfolioError,
    updatePortfolioSuccess,
    displayOutputByPortfolioSuccess
} from './portfolioSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import { actionTypes, IPortfolioWithVessels, ScopeOptions } from './portfolioTypes';
import { addPortfolioApi, deletePortfolioApi, getPortfolioByIdApi, getPortfoliosApi, updatePortfolioApi } from './portfolioApi';
import { RootState } from '../../core/store';
import { isNil } from 'lodash';
import { TSubscribedMsiVessel } from '../../user-management/subscription/subscriptionTypes';
import { setSelectedVesselsForOutputRequest } from '../search/searchSlice';
import { selectSelectedSubscriptionId, selectSubscribedMsiVessels, selectTeamId, selectUserId } from '../../user-management/appSelectors';
import { IPortfolioFormData, ISelectedVesselsFormData, IVesselFormData } from '../../shared/sharedTypes';
import { EOutputType, ESelectionType } from '../../shared/sharedEnums';
import { selectSelectionData } from '../search/searchSelectors';
import ModalManager from '../../shared/components/modal/ModalManager';
import { showSuccessAlert } from '../../services/globalAlertService';
import { 
    trackEvent,
    trackExceptionWithContext, 
    LogLevel 
} from '../../services/application-insights/appInsights';
import { 
    AppInsightsActions, 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../../services/application-insights/appInsightsConstants';


function* addPortfolioSaga(action: PayloadAction<{ portfolio: IPortfolioWithVessels }>) {
    const requestId = `portfolio-add-${Date.now()}`;
    
    try {
        trackEvent(AppInsightsActions.PORTFOLIO_CREATE_ATTEMPT, {
            requestId,
            portfolioName: action.payload.portfolio.name,
            scope: action.payload.portfolio.scope,
            vesselCount: action.payload.portfolio.portfolioVesselsImoList?.length?.toString() || '0',
            component: AppInsightsComponents.PORTFOLIO_COMPONENT
        });

        const userId: number = yield select(selectUserId);
        const teamId: number | null = action.payload.portfolio.scope === ScopeOptions.Team ? yield select(selectTeamId) : null;
        const subscriptionId: number = yield select(selectSelectedSubscriptionId);
        const newPortfolioId: number = yield call(addPortfolioApi, action.payload.portfolio, teamId, subscriptionId);
        const newPortfolio: IPortfolioWithVessels = { ...action.payload.portfolio, ...{ id: newPortfolioId, teamId: teamId, createdById: userId, isOwnPortfolio: true, isDraft: false } };
        
        trackEvent(AppInsightsActions.PORTFOLIO_CREATE_SUCCESS, {
            requestId,
            portfolioId: newPortfolioId.toString(),
            portfolioName: newPortfolio.name,
            scope: newPortfolio.scope,
            vesselCount: newPortfolio.portfolioVesselsImoList?.length?.toString() || '0',
            component: AppInsightsComponents.PORTFOLIO_COMPONENT
        });
        
        yield put(addPortfolioSuccess({ portfolio: newPortfolio }));
        showSuccessAlert(`Portfolio '${newPortfolio.name}' added successfully`);
    } catch (error: any) {
        trackExceptionWithContext(error, {
            component: AppInsightsComponents.PORTFOLIO_COMPONENT,
            action: AppInsightsActions.PORTFOLIO_CREATE_FAILURE,
            severity: LogLevel.Error,
            additionalData: {
                requestId,
                portfolioName: action.payload.portfolio.name,
                errorType: AppInsightsErrorTypes.PORTFOLIO_CRUD_ERROR,
                errorMessage: error.message || 'Unknown error'
            }
        });
        
        yield put(setPortfolioError({ message: error.message }));
    }
}

function* updatePortfolioSaga(action: PayloadAction<{ portfolio: IPortfolioWithVessels }>) {
    try {
        const userId: number = yield select(selectUserId);
        const teamId: number | null = action.payload.portfolio.scope === ScopeOptions.Team ? yield select(selectTeamId) : null;
        yield call(updatePortfolioApi, action.payload.portfolio, teamId);
        const updatedPortfolio: IPortfolioWithVessels = { ...action.payload.portfolio, ...{ teamId: teamId, createdById: action.payload.portfolio.isOwnPortfolio === true ? userId : null, isDraft: false } };
        yield put(updatePortfolioSuccess({ portfolio: updatedPortfolio }));
        showSuccessAlert(`Portfolio '${updatedPortfolio.name}' updated successfully`);
    } catch (error: any) {
        yield put(setPortfolioError({ message: error.message }));
    }
}

function* deletePortfolioSaga(action: PayloadAction<{ id: number }>) {
    try {
        yield call(deletePortfolioApi, action.payload.id);
        yield put(deletePortfolioSuccess({ deletedId: action.payload.id }));

        yield call(setSelectedVesselsAndOutputTypeAsDefaultSaga, action);

        showSuccessAlert(`Portfolio deleted successfully`);
    } catch (error: any) {
        yield put(setPortfolioError({ message: error.message }));
    }
}

function* getPortfoliosSaga() {
    try {
        const teamId: number = yield select(selectTeamId);
        const subscriptionId: number = yield select(selectSelectedSubscriptionId);
        const portfolios: IPortfolioWithVessels[] = yield call(getPortfoliosApi, teamId, subscriptionId);
        const userId: number = yield select(selectUserId);
        portfolios.forEach(portfolio => {
            portfolio.isOwnPortfolio = portfolio.createdById === userId;
            // portfolio.isDraft = false;
        });
        yield put(getPortfoliosSuccess({ portfolios }));
    } catch (error: any) {
        yield put(setPortfolioError({ message: error.message }));
    }
}

function* getPortfolioWithVesselsSaga(action: PayloadAction<{ id: number | null, tempId?: string, isDraft?: boolean }>) {
    try {
        if (action.payload.id === null) {
            yield put(setSelectedPortfolioRequest({ id: action.payload.id, tempId: action.payload.tempId, isDraft: action.payload.isDraft }));
        }
        else {
            let portfolioWithMsiVessels: IPortfolioWithVessels = yield select((state: RootState) => state.portfolioSlice.portfolios.find(p => p.id === action.payload.id && p.isDraft === false));
            if (isNil(portfolioWithMsiVessels) || isNil(portfolioWithMsiVessels?.portfolioVesselsImoList) || portfolioWithMsiVessels?.portfolioVesselsImoList.length === 0) {
                portfolioWithMsiVessels = yield call(getPortfolioByIdApi, action.payload.id);
                portfolioWithMsiVessels.isDraft = false;
                yield put(getPortfolioWithVesselsSuccess({ portfolio: portfolioWithMsiVessels }));
            } else {
                yield put(setSelectedPortfolioRequest({ id: portfolioWithMsiVessels.id }));
            }
        }
        yield call(setSelectedVesselsAndOutputTypeAsDefaultSaga, action);
    }
    catch (error: any) {
        yield put(setPortfolioError({ message: error.message }));
    }
}


function* displayOutputByPortfolioIdSaga(action: PayloadAction<{ id: number | string }>) {    
    try {
        let portfolioWithMsiVessels: IPortfolioWithVessels = yield select((state: RootState) => state.portfolioSlice.portfolios.find(p => p.id === action.payload.id || p.tempId === action.payload.id));
        if (isNil(portfolioWithMsiVessels.portfolioVesselsImoList) || portfolioWithMsiVessels.portfolioVesselsImoList.length === 0) {
            portfolioWithMsiVessels = yield call(getPortfolioByIdApi, action.payload.id as number);
            portfolioWithMsiVessels.isDraft = false;
            yield put(getPortfolioWithVesselsSuccess({ portfolio: portfolioWithMsiVessels }));
        }
        const tempSubscribedMsiVessels: TSubscribedMsiVessel[] = yield select(selectSubscribedMsiVessels);
        const vesselList = tempSubscribedMsiVessels.filter(vessel => portfolioWithMsiVessels?.portfolioVesselsImoList?.includes(vessel.imoNumber))
            .map(vessel => ({ name: vessel.vesselName, imoNumber: vessel.imoNumber, sectorName: vessel.sectorName, companyName: vessel.owner })) as IVesselFormData[];
            
        yield put(displayOutputByPortfolioSuccess());
        {// Add portfolio to recent searches
            // let recentSearches: IRecentSearchForm[] = getRecentSearchesFromLocalStorage();
            // if (recentSearches) {
            //     const existingPortfolio = recentSearches.find((portfolio) => portfolio.type === "Portfolio" && portfolio.id === action.payload.id);
            //     if (!existingPortfolio) {
            //         const { id, name, scope, isOwnPortfolio } = portfolioWithMsiVessels;
            //         recentSearches = recentSearches ? [{ id, name, scope, isOwnPortfolio, type: "Portfolio" } as IRecentSearchForm, ...recentSearches.slice(0, 5)] :
            //             [{ id, name, scope, isOwnPortfolio, type: "Portfolio" }] as IRecentSearchForm[];
            //         storeRecentSearchesInLocalStorage(recentSearches);
            //     }
            // }
        }
        yield put(setSelectedVesselsForOutputRequest({
            outputSelection: {
                selectedVesselsList: vesselList,
                selectionName: portfolioWithMsiVessels.name, selectionType: portfolioWithMsiVessels.isDraft ? ESelectionType.DraftPortfolio : ESelectionType.Portfolio,
                outputType: vesselList.length === 1 ? EOutputType.Vessel : vesselList.length > 200 ? EOutputType.Summary : EOutputType.Fleet,
                selectionTypeObject: {
                    id: portfolioWithMsiVessels.id, name: portfolioWithMsiVessels.name,
                    scope: portfolioWithMsiVessels.scope, isOwnPortfolio: portfolioWithMsiVessels.isOwnPortfolio,
                    isDraft: portfolioWithMsiVessels.isDraft, tempId: portfolioWithMsiVessels.tempId
                } as IPortfolioFormData
            }
        }))
    } catch (error: any) {
        //TODO: discuss how to manage this type of error @Muhammed @Evrim
        yield put(setPortfolioError({ message: error.message }));
    }
}

function* setSelectedVesselsAndOutputTypeAsDefaultSaga(action: PayloadAction<{ id?: number | null, tempId?: string }>) {
    try {
        const selection: ISelectedVesselsFormData = yield select(selectSelectionData);
        if ((selection.selectionTypeObject as IPortfolioFormData)?.id === action.payload.id ||
            ((selection.selectionTypeObject as IPortfolioFormData)?.tempId && (selection.selectionTypeObject as IPortfolioFormData)?.tempId === action.payload.tempId)) {
            const vessels: IVesselFormData[] = yield select(selectSubscribedMsiVessels);
            const outputType: EOutputType = vessels.length === 1 ? EOutputType.Vessel : vessels.length > 200 ? EOutputType.Summary : EOutputType.Fleet;

            const outputSelection: ISelectedVesselsFormData = {
                selectedVesselsList: vessels,
                outputType: outputType,
                selectionType: ESelectionType.None,
                selectionName: '',
            };

            yield put(setSelectedVesselsForOutputRequest({ outputSelection }));
        }
    } catch (error: any) {
        //TODO: discuss how to manage this type of error @Muhammed @Evrim
        ModalManager.show({ header: 'Error', message: JSON.stringify(error) });
    }
}

export default function* watchPortfolioActions(): Generator {
    yield takeLatest(actionTypes.ADD_PORTFOLIO_REQUEST_ACTION, addPortfolioSaga);
    yield takeLatest(actionTypes.UPDATE_PORTFOLIO_REQUEST_ACTION, updatePortfolioSaga);
    yield takeLatest(actionTypes.DELETE_PORTFOLIO_REQUEST_ACTION, deletePortfolioSaga);
    yield takeLatest(actionTypes.GET_PORTFOLIOS_REQUEST_ACTION, getPortfoliosSaga);
    yield takeLatest(actionTypes.GET_PORTFOLIO_WITH_VESSELS_REQUEST_ACTION, getPortfolioWithVesselsSaga);
    yield takeLatest(actionTypes.DISPLAY_OUTPUT_BY_PORTFOLIO_ID_REQUEST_ACTION, displayOutputByPortfolioIdSaga);
    yield takeLatest(actionTypes.DELETE_DRAFT_PORTFOLIO_REQUEST_ACTION, setSelectedVesselsAndOutputTypeAsDefaultSaga);
    // yield takeLatest(actionTypes.DISPLAY_OUTPUT_BY_PORTFOLIO_IMO_LIST_REQUEST_ACTION, displayOutputByPortfolioImoListSaga);
}

